import { getCombinationName } from '../getCombinationName';
import { chain } from 'lodash';
import { useCallback } from 'react';
import { v4 as uuuidv4 } from 'uuid';
import { FormCombination, FormWordGroup } from '../../models/ComboFormState';
import { SetFormState } from '../../models/SetFormState';

export function useAddGroup(setFormState: SetFormState) {
  return useCallback((): string => {
    const id = uuuidv4();
    setFormState((prevState) => {
      //Add new word group
      const newGroup: FormWordGroup = {
        name: String.fromCharCode(65 + prevState.wordGroups.length),
        order: prevState.wordGroups.length,
        id: id,
        words: '',
      };

      //Add new word group to list of existing word groups
      const wordGroups = chain([...prevState.wordGroups, newGroup])
        .orderBy([(wg) => wg.order], ['asc'])
        .map((wg, index) => ({ ...wg, order: index }))
        .value();

      // Create word group combinations using the word group ids
      const singleCombinations = wordGroups.map((wg) => [wg.id]);

      let doubleCombinations: string[][] = [];
      if (wordGroups.length > 1) {
        doubleCombinations = wordGroups.flatMap((wg) => {
          const otherGroups = wordGroups.filter((sG) => sG.id !== wg.id);
          return otherGroups.map((oG) => [wg.id, oG.id]);
        });
      }

      let trippleCombinations: string[][] = [];
      if (wordGroups.length > 2) {
        trippleCombinations = wordGroups.flatMap((wg) => {
          const firstOtherGroups = wordGroups.filter((sG1) => sG1.id !== wg.id);
          return firstOtherGroups.flatMap((oG1) => {
            const secondOtherGroups = firstOtherGroups.filter((sG2) => sG2.id !== oG1.id);
            return secondOtherGroups.map((oG2) => [wg.id, oG1.id, oG2.id]);
          });
        });
      }

      const allWordGroupCombinationsIds = [
        ...singleCombinations,
        ...doubleCombinations,
        ...trippleCombinations,
      ];

      const oldCombinationsIds = prevState.comboCombinations.map((wgC) =>
        wgC.wordGroups.map((wg) => wg.id)
      );

      //Compare list of all word group combinations with the old list. Create a list with new combinations.
      const newCombinations = allWordGroupCombinationsIds.filter(
        (aC) => !oldCombinationsIds.some((oC) => JSON.stringify(oC) === JSON.stringify(aC))
      );

      const newComboCombinationsWithSpacing = newCombinations.map((wgC): FormCombination => {
        const combination = {
          id: uuuidv4(),
          selected: false,
          wordGroups: wgC.map((wg, index) => ({
            id: wg,
            order: index,
          })),
          name: '',
          removeSpacing: 0,
        };

        combination.name = getCombinationName(combination, wordGroups);
        return combination;
      });
      const newComboCombinationsRemoveFirstSpacing = newCombinations
        .filter((cc) => cc.length >= 2)
        .map((wgC): FormCombination => {
          const combination = {
            id: uuuidv4(),
            selected: false,
            wordGroups: wgC.map((wg, index) => ({
              id: wg,
              order: index,
            })),
            name: '',
            removeSpacing: 1,
          };

          combination.name = getCombinationName(combination, wordGroups);
          return combination;
        });
      const newComboCombinationsRemoveSecondSpacing = newCombinations
        .filter((cc) => cc.length >= 3)
        .map((wgC): FormCombination => {
          const combination = {
            id: uuuidv4(),
            selected: false,
            wordGroups: wgC.map((wg, index) => ({
              id: wg,
              order: index,
            })),
            name: '',
            removeSpacing: 2,
          };

          combination.name = getCombinationName(combination, wordGroups);
          return combination;
        });

      const concatCombinations = [
        ...prevState.comboCombinations,
        ...newComboCombinationsWithSpacing,
        ...newComboCombinationsRemoveFirstSpacing,
        ...newComboCombinationsRemoveSecondSpacing,
      ];

      return {
        ...prevState,
        wordGroups: wordGroups,
        comboCombinations: concatCombinations,
      };
    });
    return id;
  }, [setFormState]);
}
